import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Car, Make, Model, AttachmentStatus, AdditionalFeesMap, FeeType } from '../../types';
import { carService, modelSearchService } from '../../services';
import { RequiredIndicator } from '../ui';
import { AsyncSearchSelect } from '../forms';
import { withoutInsuranceFee } from '../../utils/carUtils';

interface AdditionalFee {
    description: string;
    cost: number;
    type: FeeType;
}

interface RelatedCarEntry {
    id?: string; // For existing entries
    relation: AttachmentStatus;
    chassisNumber: string;
    makeId: string;
    modelId: string;
    manualMakeModel?: string; // Manual entry for make/model
    modelDisplayValue?: string; // Cached display value for make & model
    existingCarId?: string; // If chassis matches existing car
    remarks?: string;
    additionalFees?: AdditionalFeesMap;
}

interface RelatedCarsSectionProps {
    mainCarId?: string; // ID of the main car being edited
    voyageId: string;
    shipperId?: string; // Shipper ID to filter chassis search to same shipper
    relatedCars: RelatedCarEntry[];
    onRelatedCarsChange: (cars: RelatedCarEntry[]) => void;
    fieldErrors?: Record<string, string>;
}

export const RelatedCarsSection: React.FC<RelatedCarsSectionProps> = ({
    mainCarId,
    voyageId,
    shipperId,
    relatedCars,
    onRelatedCarsChange,
    fieldErrors = {}
}) => {
    const [chassisSearchResults, setChassisSearchResults] = useState<Car[]>([]);
    const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(null);
    const [manualEntryModes, setManualEntryModes] = useState<Record<number, boolean>>({});
    const [shipperMismatchError, setShipperMismatchError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const newEntryRef = useRef<HTMLInputElement>(null);
    const [pendingScrollToNew, setPendingScrollToNew] = useState(false);

    // Initialize manual entry modes based on existing data
    useEffect(() => {
        const modes: Record<number, boolean> = {};
        relatedCars.forEach((entry, index) => {
            if (entry.manualMakeModel) {
                modes[index] = true;
            }
        });
        setManualEntryModes(prev => ({ ...prev, ...modes }));
    }, [relatedCars]);

    const toggleManualEntry = (index: number) => {
        setManualEntryModes(prev => ({ ...prev, [index]: !prev[index] }));
        // Don't clear values when toggling - just switch view mode
        // The backend handles which value to use (manual vs FK)
    };

    // Load models from backend
    const loadModels = useCallback(async (query: string): Promise<Array<{ model: Model; make: Make }>> => {
        try {
            const results = await modelSearchService.searchWithMake(query, 50);
            return results.map(result => ({
                model: {
                    id: result.id,
                    code: result.code,
                    name: result.name,
                    type: result.type,
                    weight: result.weight,
                    length: result.length,
                    width: result.width,
                    height: result.height,
                    cbm: result.cbm,
                    make_id: result.make_id,
                } as Model,
                make: {
                    id: result.make.id,
                    code: result.make.code,
                    name: result.make.name,
                    created_at: '',
                    creator_id: '',
                } as Make
            }));
        } catch (error: any) {
            if (error.name !== 'AbortError') {
                console.error('Model search error:', error);
            }
            return [];
        }
    }, []);

    // Auto-scroll and focus when a new entry is added
    useEffect(() => {
        if (pendingScrollToNew && newEntryRef.current) {
            newEntryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            newEntryRef.current.focus();
            setPendingScrollToNew(false);
        }
    }, [pendingScrollToNew, relatedCars.length]);

    const handleAddRelatedCar = () => {
        // Check if there's already an empty entry (no chassis number)
        const hasEmptyEntry = relatedCars.some(car => !car.chassisNumber.trim());
        if (hasEmptyEntry) {
            // Find and scroll to the empty entry
            const emptyIndex = relatedCars.findIndex(car => !car.chassisNumber.trim());
            const emptyInput = containerRef.current?.querySelector(`[data-entry-index="${emptyIndex}"]`) as HTMLInputElement;
            if (emptyInput) {
                emptyInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                emptyInput.focus();
            }
            return;
        }

        const newEntry: RelatedCarEntry = {
            relation: 'STC',
            chassisNumber: '',
            makeId: '',
            modelId: '',
            manualMakeModel: '',
            remarks: '',
            additionalFees: {}
        };
        onRelatedCarsChange([...relatedCars, newEntry]);
        setPendingScrollToNew(true);
    };

    const handleRemoveRelatedCar = (index: number) => {
        onRelatedCarsChange(relatedCars.filter((_, i) => i !== index));
    };

    const handleUpdateRelatedCar = (index: number, updates: Partial<RelatedCarEntry>) => {
        const updated = [...relatedCars];
        updated[index] = { ...updated[index], ...updates };
        onRelatedCarsChange(updated);
    };

    const searchChassisNumber = async (query: string) => {
        if (!query || query.length < 3) {
            setChassisSearchResults([]);
            return;
        }

        try {
            // Use the search endpoint which properly handles chassis_number search
            // Search all cars (not filtered by shipper) - validation happens when user selects
            const searchParams: { limit: number } = { limit: 20 };
            const results = await carService.search(query, 'chassis_number', searchParams);
            // Filter out cars that are already attached to avoid duplicates
            const availableCars = results.filter(car => !car.attachment_status);
            setChassisSearchResults(availableCars);
        } catch (error) {
            console.error('Error searching chassis numbers:', error);
            setChassisSearchResults([]);
        }
    };

    const handleChassisNumberChange = async (index: number, chassisNumber: string) => {
        handleUpdateRelatedCar(index, { chassisNumber, existingCarId: undefined });
        setShipperMismatchError(null); // Clear any previous error
        setActiveSearchIndex(index); // Track which entry is being searched

        if (chassisNumber.length >= 3) {
            await searchChassisNumber(chassisNumber);
        } else {
            setChassisSearchResults([]);
        }
    };

    const handleSelectExistingCar = (index: number, car: Car) => {
        // Validate shipper match - only allow selecting cars from the same shipper
        if (shipperId && car.shipper_id && car.shipper_id !== shipperId) {
            const shipperName = car.shipper?.shortname || car.shipper?.name || car.shipper?.code || 'a different shipper';
            setShipperMismatchError(`Cannot select this car - it belongs to ${shipperName}. Related units must belong to the same shipper.`);
            return;
        }

        // Clear any previous error
        setShipperMismatchError(null);

        // Build display value from the car's make and model objects
        let modelDisplayValue = '';
        if (car.make && car.model) {
            modelDisplayValue = `${car.make.name} - ${car.model.name}`;
        } else if (car.make) {
            modelDisplayValue = car.make.name;
        } else if (car.model) {
            modelDisplayValue = car.model.name;
        }

        const hasDbMakeModel = !!(car.make_id || car.model_id);
        const inheritedManualMakeModel = !hasDbMakeModel ? (car.manual_make_model || '') : '';

        handleUpdateRelatedCar(index, {
            chassisNumber: car.chassis_number,
            makeId: car.make_id,
            modelId: car.model_id,
            modelDisplayValue,
            existingCarId: car.id,
            remarks: car.remarks || '',
            additionalFees: withoutInsuranceFee(car.additional_fees), // related units don't carry insurance
            manualMakeModel: inheritedManualMakeModel
        });
        setChassisSearchResults([]);
        setActiveSearchIndex(null);
        // Switch to manual mode if the selected car uses manual make/model, otherwise DB mode
        setManualEntryModes(prev => ({ ...prev, [index]: !hasDbMakeModel && !!inheritedManualMakeModel }));
    };

    const handleSelectModel = (index: number, option: { model: Model; make: Make }) => {
        if (!option.make) return;
        handleUpdateRelatedCar(index, {
            makeId: option.make.id,
            modelId: option.model.id,
            modelDisplayValue: `${option.make.name} - ${option.model.name}`,
            manualMakeModel: '' // Clear manual entry when selecting from DB
        });
    };

    const getModelInitialValue = (entry: RelatedCarEntry) => {
        // If we have a cached display value (e.g., from selecting an existing car), use it
        if (entry.modelDisplayValue) {
            return entry.modelDisplayValue;
        }
        return '';
    };

    const getAdditionalFeesList = (fees: AdditionalFeesMap | undefined): AdditionalFee[] => {
        return Object.entries(fees || {}).map(([description, value]) => ({
            // Show empty string for placeholder keys (starting with __new_fee_)
            description: description.startsWith('__new_fee_') ? '' : description,
            cost: typeof value === 'object' ? value.amount : value,
            type: (typeof value === 'object' ? value.type : 'F') as FeeType
        }));
    };

    const handleAddFee = (index: number) => {
        const entry = relatedCars[index];
        // Copy existing fees as-is to preserve original keys
        const feesMap: AdditionalFeesMap = { ...(entry.additionalFees || {}) };
        // Add new fee with a placeholder key
        const newFeeKey = `__new_fee_${Date.now()}`;
        feesMap[newFeeKey] = { amount: 0, type: 'F' };
        handleUpdateRelatedCar(index, { additionalFees: feesMap });
    };

    const handleRemoveFee = (entryIndex: number, feeIndex: number) => {
        const entry = relatedCars[entryIndex];
        // Get raw fees from the map to preserve original keys
        const rawFees = Object.entries(entry.additionalFees || {});
        const newFees = rawFees.filter((_, i) => i !== feeIndex);
        const feesMap: AdditionalFeesMap = {};
        newFees.forEach(([key, value]) => {
            feesMap[key] = value;
        });
        handleUpdateRelatedCar(entryIndex, { additionalFees: feesMap });
    };

    const handleFeeChange = (entryIndex: number, feeIndex: number, field: 'description' | 'cost' | 'type', value: string | number | FeeType) => {
        const entry = relatedCars[entryIndex];
        // Get raw fees to preserve original keys
        const rawFees = Object.entries(entry.additionalFees || {});
        const feesMap: AdditionalFeesMap = {};
        
        rawFees.forEach(([originalKey, feeValue], idx) => {
            const amount = typeof feeValue === 'object' ? feeValue.amount : feeValue;
            const type = (typeof feeValue === 'object' ? feeValue.type : 'F') as FeeType;
            
            if (idx === feeIndex) {
                // This is the fee being changed
                if (field === 'description') {
                    // Use new description as key - preserve spaces, only use placeholder if completely empty
                    const newKey = (value as string) || `__new_fee_${Date.now()}`;
                    feesMap[newKey] = { amount, type };
                } else if (field === 'cost') {
                    feesMap[originalKey] = { amount: value as number, type };
                } else if (field === 'type') {
                    feesMap[originalKey] = { amount, type: value as FeeType };
                }
            } else {
                // Keep other fees unchanged
                feesMap[originalKey] = { amount, type };
            }
        });
        
        handleUpdateRelatedCar(entryIndex, { additionalFees: feesMap });
    };

    return (
        <div ref={containerRef} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2 pb-1 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                    Related Units
                </h3>
                <button
                    type="button"
                    onClick={handleAddRelatedCar}
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-md transition-colors flex items-center"
                >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                    </svg>
                    Add Entry
                </button>
            </div>

            {relatedCars.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No related units added. Click "Add Entry" to add one.
                </p>
            ) : (
                <div className="grid grid-cols-2 gap-4 max-h-[500px] overflow-y-auto">
                    {relatedCars.map((entry, index) => {
                        const feesList = getAdditionalFeesList(entry.additionalFees);
                        const totalFees = feesList.reduce((sum, fee) => sum + (fee.cost || 0), 0);
                        
                        return (
                            <div key={index} className="border-[3px] border-gray-400 dark:border-gray-500 rounded-lg overflow-hidden shadow-sm">
                                <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-700 px-3 py-2 border-b-[3px] border-gray-400 dark:border-gray-500">
                                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                        Entry {index + 1}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveRelatedCar(index)}
                                        className="text-red-600 hover:text-red-700 text-xs font-medium"
                                    >
                                        Remove
                                    </button>
                                </div>
                                <div className="p-2 space-y-2">

                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                                            Relation <RequiredIndicator/>
                                        </label>
                                        <select
                                            value={entry.relation}
                                            onChange={(e) => handleUpdateRelatedCar(index, {
                                                relation: e.target.value as AttachmentStatus
                                            })}
                                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                        >
                                            <option value="STC">STC</option>
                                            <option value="Attached">Attached</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                                            Chassis Number <RequiredIndicator/>
                                        </label>
                                        <div className="relative">
                                            <input
                                                ref={index === relatedCars.length - 1 ? newEntryRef : undefined}
                                                data-entry-index={index}
                                                type="text"
                                                value={entry.chassisNumber}
                                                onChange={(e) => e.target.value.length <= 50 && handleChassisNumberChange(index, e.target.value)}
                                                placeholder="Enter or search chassis number..."
                                                className="w-full px-2 py-1 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                            />
                                            {entry.existingCarId ? (
                                                <span className="absolute right-2 top-1.5 text-xs text-green-600 dark:text-green-400">
                                                    ✓ Existing
                                                </span>
                                            ) : (
                                                <span className={`absolute right-2 top-1.5 text-xs font-medium ${
                                                    entry.chassisNumber.length === 17 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                                                }`}>
                                                    {entry.chassisNumber.length}
                                                </span>
                                            )}
                                            {chassisSearchResults.length > 0 && activeSearchIndex === index && !entry.existingCarId && (
                                                <div className="absolute top-full left-0 right-0 mt-1 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 shadow-lg max-h-40 overflow-y-auto z-50">
                                                    {chassisSearchResults.map((car) => (
                                                        <button
                                                            key={car.id}
                                                            type="button"
                                                            onClick={() => handleSelectExistingCar(index, car)}
                                                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                                                        >
                                                            <div className="font-medium text-gray-900 dark:text-gray-100">{car.chassis_number}</div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                {car.make?.code && car.model?.code
                                                                    ? `${car.make.code}/${car.model.code}`
                                                                    : (car.manual_make_model || '-')
                                                                } - {car.name}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {shipperMismatchError && activeSearchIndex === index && (
                                                <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md z-50">
                                                    <p className="text-xs text-red-600 dark:text-red-400">{shipperMismatchError}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                            Make & Model
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => toggleManualEntry(index)}
                                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                        >
                                            {manualEntryModes[index] ? 'Search Database' : 'Manual Entry'}
                                        </button>
                                    </div>
                                    {manualEntryModes[index] ? (
                                        <input
                                            type="text"
                                            value={entry.manualMakeModel || ''}
                                            onChange={(e) => handleUpdateRelatedCar(index, { manualMakeModel: e.target.value })}
                                            placeholder="e.g., Generator, Crane basket..."
                                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    ) : (
                                        <AsyncSearchSelect<{ model: Model; make: Make }>
                                            loadOptions={loadModels}
                                            getValue={(option) => option.model.id}
                                            getLabel={(option) => `${option.make.name} - ${option.model.name}`}
                                            getSecondaryLabel={(option) => `${option.model.type} | ${option.model.weight}kg | ${option.model.length}×${option.model.width}×${option.model.height}m`}
                                            onSelect={(option) => handleSelectModel(index, option)}
                                            value={entry.modelId}
                                            initialDisplayValue={getModelInitialValue(entry)}
                                            placeholder="Search by make or model..."
                                            noOptionsMessage="No models found"
                                        />
                                    )}
                                </div>

                                {/* Additional Fees Section */}
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                            Additional Fees
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => handleAddFee(index)}
                                            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded-md"
                                        >
                                            + Add Fee
                                        </button>
                                    </div>
                                    <div className="space-y-1 border border-gray-200 dark:border-gray-600 rounded-md p-2 max-h-24 overflow-y-auto">
                                        {feesList.length === 0 ? (
                                            <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-1">
                                                No additional fees
                                            </div>
                                        ) : (
                                            feesList.map((fee, feeIndex) => (
                                                <div key={feeIndex} className="flex items-center space-x-2">
                                                    <input
                                                        type="text"
                                                        value={fee.description}
                                                        onChange={(e) => handleFeeChange(index, feeIndex, 'description', e.target.value)}
                                                        placeholder="Description..."
                                                        className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                                    />
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={fee.cost || ''}
                                                        onChange={(e) => handleFeeChange(index, feeIndex, 'cost', Number(e.target.value))}
                                                        placeholder="Cost"
                                                        className="w-20 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                    />
                                                    <div className="flex items-center space-x-1 px-1">
                                                        <label className="flex items-center space-x-0.5 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={fee.type === 'F' || fee.type === 'B'}
                                                                onChange={(e) => {
                                                                    const isFreightChecked = e.target.checked;
                                                                    const isCollectChecked = fee.type === 'C' || fee.type === 'B';
                                                                    let newType: FeeType;
                                                                    if (isFreightChecked && isCollectChecked) newType = 'B';
                                                                    else if (isFreightChecked) newType = 'F';
                                                                    else if (isCollectChecked) newType = 'C';
                                                                    else newType = 'F';
                                                                    handleFeeChange(index, feeIndex, 'type', newType);
                                                                }}
                                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3"
                                                            />
                                                            <span>F</span>
                                                        </label>
                                                        <label className="flex items-center space-x-0.5 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={fee.type === 'C' || fee.type === 'B'}
                                                                onChange={(e) => {
                                                                    const isCollectChecked = e.target.checked;
                                                                    const isFreightChecked = fee.type === 'F' || fee.type === 'B';
                                                                    let newType: FeeType;
                                                                    if (isFreightChecked && isCollectChecked) newType = 'B';
                                                                    else if (isFreightChecked) newType = 'F';
                                                                    else if (isCollectChecked) newType = 'C';
                                                                    else newType = 'F';
                                                                    handleFeeChange(index, feeIndex, 'type', newType);
                                                                }}
                                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3"
                                                            />
                                                            <span>C</span>
                                                        </label>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveFee(index, feeIndex)}
                                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    {feesList.length > 0 && (
                                        <div className="text-xs text-gray-700 dark:text-gray-300 mt-1 text-right">
                                            Total: <span className="font-semibold">{totalFees.toFixed(2)}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Remarks Section */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Remarks
                                    </label>
                                    <textarea
                                        value={entry.remarks || ''}
                                        onChange={(e) => handleUpdateRelatedCar(index, { remarks: e.target.value })}
                                        placeholder="Enter remarks..."
                                        rows={2}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-vertical"
                                    />
                                </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

